<div class="flex flex-col h-screen md:pl-10 md:pr-[96px] md:pt-8 pb-10 relative" [appScreenWakeLock]="screenWakeLockActive" (wakeLockUnavailable)="onWakeLockUnavailable($event)">
  @if (loadingDictation) {
  <app-loading [label]="'Loading encounter ...' | translate" size="lg"></app-loading>
  } @else {
  <div class="flex items-center mb-2 hidden md:flex">
    <button (click)="onBreadcrumbHome()"
      class="label-medium cursor-pointer px-2 py-1 rounded-md hover:bg-surface text-outline">
      {{ 'Home' | translate }}
    </button>
    @if (fromEncounters) {
      <span class="material-icons !text-outline-variant !text-lg">chevron_right</span>
      <button (click)="onBreadcrumbEncounters()"
        class="label-medium cursor-pointer px-2 py-1 rounded-md hover:bg-surface text-outline">
        {{ 'Encounters' | translate }}
      </button>
    }
    <span class="material-icons !text-outline-variant !text-lg">chevron_right</span>
    <span class="label-medium text-outline px-2">
      {{ dictation?.copilot?.subject_name || ('No patient' | translate)}}
    </span>
  </div>
  <!-- Page Header -->
  <div class="flex w-full px-4 pt-4 md:pt-0 md:px-0 bg-surface md:bg-transparent rounded-b-lg pb-2 md:pb-0">
    <!-- Back to home -->
    <div class="md:hidden pt-2.5">
      <button mat-icon-button [attr.aria-label]="(fromEncounters ? 'Back to encounters' : 'Back to home') | translate"
        (click)="fromEncounters ? onBreadcrumbEncounters() : onBreadcrumbHome()">
        <mat-icon>arrow_back</mat-icon>
      </button>
    </div>
    <div class="grid grid-flow-row md:grid-flow-col md:grid-rows-[auto_auto] pl-1 md:pr-3 md:grid-cols-[auto_1fr] w-full">
      <!-- Patient Name -->
      <div class="w-fit order-1 mr-2 pl-2  md:pt-2 min-w-0 flex items-center" [ngClass]="
        {'cursor-pointer hover:bg-surface rounded-lg': canEditPatient}"
        (click)="canEditPatient && openPatientEditor()">
        <p class="text-on-surface title-large md:headline-large md:text-primary">
          {{ patientDisplayName }}
          @if (canEditPatient) {
            <mat-icon svgIcon="edit-pencil" class="icon-size [--icon-size:18px] md:[--icon-size:24px] inline-block align-middle ml-1 md:ml-1 -translate-y-0.5"></mat-icon>
          }
        </p>
      </div>
      <!-- Actions -->
      <div class="flex justify-end items-center md:items-start order-2 md:order-3">
        @if (showResumeConsultation) {
          <app-resume-consultation-button
            [disabled]="isResumeConsultationDisabled"
            [tooltip]="resumeDisabledReason"
            (resumeClicked)="goToEncounter()" />
        }
        <ng-container *ngIf="hasDocuments && (!hasApprovedDocuments() || canRegenerateAllDocuments)">
          <ng-container *ngTemplateOutlet="documentMoreMenu; context: { class: 'md:!hidden' }"></ng-container>
        </ng-container>
      </div>
      <!-- Patient ID / Age / DOB -->
      <div class="col-span-2 md:col-span-1 order-3 md:order-2 flex items-center gap-2 text-on-surface-variant body-medium md:text-primary pl-2">
        <span *ngIf="patientId">{{ patientId }}</span>
        <span *ngIf="!isLegalOrg && patientId && patientAge != null">•</span>
        <span *ngIf="!isLegalOrg && patientAge != null">{{ patientAge }} {{ 'yrs' | translate }}</span>
        <span *ngIf="!isLegalOrg && patientAge != null && patientDobLabel">•</span>
        <span *ngIf="!isLegalOrg && patientDobLabel">{{ patientDobLabel }}</span>
      </div>
      <!-- Date and More Actions -->
      <div class="col-span-2 md:col-span-none flex md:justify-end items-center pt-0 md:pt-0 pl-2 md:pl-0 order-4">
        <div class="flex items-center gap-2 body-medium text-on-surface-variant">
          @if (consultationLocation) {
            <span class="flex items-center gap-1.5" data-testid="consultation-location">
              <mat-icon svgIcon="building" class="icon-size [--icon-size:18px]"
                [attr.aria-label]="'Location' | translate"></mat-icon>
              {{ consultationLocation }}
            </span>
            <span>•</span>
          }
          <span data-testid="encounter-date">{{ encounterDate }}</span>
        </div>
        <ng-container *ngIf="hasDocuments && (!hasApprovedDocuments() || canRegenerateAllDocuments)">
          <ng-container *ngTemplateOutlet="documentMoreMenu; context: { class: '!hidden md:!block' }"></ng-container>
        </ng-container>
      </div>
    </div>
  </div>

  <!-- Two-column area (from patient details to end of screen) -->
  <div class="flex-1 min-h-0 grid gap-4 mt-5 md:mt-5 grid-cols-1 px-0"
    [ngClass]="{ 'md:grid-cols-[minmax(0,1fr)_auto]': rightPaneOpen }">
    <!-- Left column: documents UI (tabs, toolbar, editor, rating) -->
    <div class="min-h-0 h-full flex flex-col bg-white overflow-hidden" data-testid="document-editor-container">
      <div
        class="flex-1 min-h-0 flex flex-col"
        [ngClass]="{ 'md:border md:border-linear-stroke md:rounded-[8px] md:overflow-hidden': hasDocuments }">
      <!-- Template Tabs and Actions -->
      <div *ngIf="hasDocuments" class="editor-header h-14 shrink-0 flex items-center bg-surface border-b border-linear-stroke overflow-hidden">
        <!-- Tab Navigation -->
        <div class="flex items-center w-full gap-2 pl-2.5 md:pl-0 pr-1">
          <!-- Material Tabs -->
          <div class="overflow-x-auto hidden md:flex min-w-0 flex-1">
            <mat-tab-group [selectedIndex]="selectedTemplateIndex" (selectedIndexChange)="onTemplateChange($event)"
              [mat-stretch-tabs]="false" class="custom-tabs h-14 min-w-0 [&_.mat-mdc-tab-header]:h-14 [&_.mat-mdc-tab-header]:bg-surface [&_.mat-mdc-tab-body-wrapper]:hidden">
              <mat-tab *ngFor="let document of processedDocuments; let i = index; trackBy: trackByDocumentKey">
                <ng-template mat-tab-label>
                  <span class="flex items-center gap-2" [class.text-on-surface-variant]="selectedTemplateIndex !== i">
                    <app-loading-spinner [loading]="isDocumentGenerating(document)" [diameter]="16"></app-loading-spinner>
                    <mat-icon class="!text-base text-error"
                      *ngIf="!isDocumentGenerating(document) && document?.error">warning</mat-icon>
                    <span>{{ document.templateTitle }}</span>
                  </span>
                </ng-template>
              </mat-tab>
            </mat-tab-group>
          </div>
          <div class="md:hidden w-full">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full document-selector">
              <mat-select [value]="selectedTemplateIndex" (valueChange)="onTemplateChange($event)">
                <mat-option *ngFor="let document of processedDocuments; let i = index; trackBy: trackByDocumentKey" [value]="i">
                  {{ document.templateTitle }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <span
            class="inline-flex"
            [appTooltip]="(isSmartDictationActive ? 'Only one document can be created in smart dictation mode' : 'Add') | translate"
            appTooltipPosition="above"
            appTooltipAlign="end">
            <button mat-icon-button (click)="addNewTemplate()" [disabled]="isRegenerating || !retention.canGenerate || isSmartDictationActive"
              class="w-8 h-8 rounded-full bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary)]/90 transition-colors duration-200 items-center justify-center">
              <mat-icon>add</mat-icon>
            </button>
          </span>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col relative"
        [ngClass]="{ 'md:border md:border-linear-stroke md:rounded-[8px]': !hasDocuments }">
        <!-- Empty State -->
        <div
          class="flex-1 flex items-center justify-center"
          *ngIf="!hasDocuments">
          <div class="flex flex-col gap-6 items-center justify-center max-w-md py-10 px-4">
            <!-- Folder Icon -->
            <div class="relative w-28 h-28">
              <img src="/assets/folder_empty.svg" alt="No documents" class="w-full h-full" />
            </div>
            
            <!-- Copy -->
            <div class="flex flex-col gap-3 items-start w-full">
              <!-- Title -->
              <div class="flex gap-1 items-start justify-center w-full">
                <div class="flex gap-1.5 items-center justify-center overflow-clip">
                  <p class="title-large text-primary text-center w-full">
                    {{ 'No documents remaining' | translate }}
                  </p>
                </div>
              </div>
              <!-- Description -->
              <div class="body-medium text-on-surface-variant text-center w-full">
                <p>{{ 'There are no generated documents left, do you want to create a new one?' | translate }}</p>
              </div>
            </div>
            
            <!-- Buttons -->
            <div class="flex gap-2 md:gap-6 items-start justify-center w-full">
              <button mat-stroked-button (click)="goHome()" 
                class="!h-9 !px-3 !md:px-6 !rounded-lg tpro-buttom-small md:w-full shrink-0 md:shrink-1">
                {{ 'Leave empty' | translate }}
              </button>
              <button mat-flat-button color="primary" (click)="addNewTemplate()" [disabled]="isRegenerating || !retention.canGenerate"
                class="!h-9 !py-2 !rounded-lg !bg-primary !text-on-primary tpro-buttom-small w-full">
                <mat-icon class="!w-[18px] !h-[18px] !text-[18px] !text-on-primary">add</mat-icon>
                {{ 'New document' | translate }}
              </button>
            </div>
            
            <!-- Delete Session Button -->
            <button *ngIf="!hasApprovedDocuments()" mat-button (click)="handleDeleteEncounter()" 
              class="!h-9 !px-6 !py-2 label-large">
              {{ 'Delete entire session' | translate }}
            </button>
          </div>
        </div>
        <!-- Editor Section: Normal -->
        <div
          class="flex-1 flex flex-col min-h-0 overflow-hidden relative"
          *ngIf="hasDocuments && !selectedDocument?.error">
          <!-- Wave indicator for active transcription (fixed at top-right) -->
          <div *ngIf="isTranscriptionStreaming" class="absolute right-6 top-15 z-20">
            <app-wave-indicator></app-wave-indicator>
          </div>
          <!-- Editor Toolbar -->
          <div class="border-b border-gray-200">
            <!-- Row 1: sync (left) | priority, approve, more (right) -->
            <div class="flex items-center gap-1 pl-2 pr-1 py-2.5 min-h-12">
              <app-document-sync-toolbar
                *ngIf="showDocumentSyncToolbar"
                class="hidden md:inline-flex"
                [updatedLabel]="documentSyncUpdatedLabel"
                [canSync]="canSyncSelectedDocument"
                [isSyncing]="isSelectedDocumentSyncing"
                (syncRequested)="onSyncSelectedDocument()">
              </app-document-sync-toolbar>
              <div class="flex items-center gap-1 w-full md:w-auto md:ml-auto justify-end">
                <ng-container *ngIf="showPrioritySelector && !selectedDocument?.approved">
                  <button mat-stroked-button [matMenuTriggerFor]="priorityMenu" [disabled]="isRegenerating"
                    class="rounded-full border-outline text-on-surface flex flex-row items-center !h-9">
                    <span class="flex items-center gap-1">
                      <span class="inline-block w-2 h-2 rounded-full"
                        [ngStyle]="{ 'background-color': getDotColor(1) }"></span>
                      <span class="inline-block w-2 h-2 rounded-full"
                        [ngStyle]="{ 'background-color': getDotColor(2) }"></span>
                      <span class="inline-block w-2 h-2 rounded-full"
                        [ngStyle]="{ 'background-color': getDotColor(3) }"></span>
                      <span class="body-medium ml-2">{{ priorityLabel }}</span>
                    </span>
                    <mat-icon>expand_more</mat-icon>
                  </button>
                </ng-container>
                <ng-container *ngIf="hasDocuments && !selectedDocument?.approved && user.entity.settings.enableDictate === true; else approvedChip">
                  <div class="inline-flex">
                    <button mat-flat-button color="primary" (click)="approveDocument()" [disabled]="isRegenerating || isSaving"
                      class="!h-9 !text-sm">
                      {{ 'Approve' | translate }}
                    </button>
                  </div>
                </ng-container>
                <ng-template #approvedChip>
                  <span *ngIf="selectedDocument?.approved && user.entity.settings.enableDictate === true"
                    class="inline-flex items-center justify-center !h-9 px-4 rounded-full border body-small text-secondary-30 bg-secondary-98 border-secondary-80 w-full text-center md:mr-1">
                    {{ 'Approved' | translate }}
                  </span>
                </ng-template>
                <div class="inline-flex" *ngIf="hasDocuments && !selectedDocument?.approved">
                  <button mat-icon-button [matMenuTriggerFor]="moreMenu" [disabled]="isRegenerating"
                    class="text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)] hover:bg-[var(--color-surface-container)] transition-colors duration-200">
                    <mat-icon svgIcon="more-vert"></mat-icon>
                  </button>
                </div>
              </div>
            </div>
            <div class="w-full border-t border-linear-stroke"></div>
            <!-- Row 2: recipients (left) | formatting, copy (right) -->
            <div class="flex items-center gap-1 pl-2 pr-1 py-1 min-h-12">
              <app-recipient-fields
                class="shrink-0 min-w-0"
                [recipients]="visibleRecipients"
                [enabled]="recipientFieldsEnabled"
                [readOnly]="recipientFieldsReadOnly"
                (toClick)="openRecipientEditor('primary', recipientFieldsReadOnly)"
                (ccClick)="openRecipientEditor('cc', recipientFieldsReadOnly)"
                (viewCc)="openRecipientEditor('cc', recipientFieldsReadOnly)"
                (removePrimary)="removePrimaryRecipient()"
                (removeCc)="removeCcRecipient()">
              </app-recipient-fields>
              <div class="flex items-center gap-1 ml-auto">
                <div class="flex [&>button]:max-md:!size-8">
                  <button mat-icon-button (click)="toggleBold()" [matTooltip]="'Bold' | translate" [disabled]="isRegenerating || !!selectedDocument?.approved"
                    [class.bg-primary-container]="editor && editor.isActive('bold')">
                    <mat-icon>format_bold</mat-icon>
                  </button>
                  <button mat-icon-button (click)="toggleItalic()" [matTooltip]="'Italic' | translate" [disabled]="isRegenerating || !!selectedDocument?.approved"
                    [class.bg-primary-container]="editor && editor.isActive('italic')">
                    <mat-icon>format_italic</mat-icon>
                  </button>
                  <button mat-icon-button (click)="toggleUnderline()" [matTooltip]="'Underline' | translate" [disabled]="isRegenerating || !!selectedDocument?.approved"
                    [class.bg-primary-container]="editor && editor.isActive('underline')">
                    <mat-icon>format_underlined</mat-icon>
                  </button>
                  <button mat-icon-button (click)="toggleBulletList()" [matTooltip]="'Bullet list' | translate" [disabled]="isRegenerating || !!selectedDocument?.approved"
                    [class.bg-primary-container]="editor && editor.isActive('bulletList')">
                    <mat-icon>format_list_bulleted</mat-icon>
                  </button>
                  <button mat-icon-button (click)="toggleOrderedList()" [matTooltip]="'Numbered list' | translate" [disabled]="isRegenerating || !!selectedDocument?.approved"
                    [class.bg-primary-container]="editor && editor.isActive('orderedList')">
                    <mat-icon>format_list_numbered</mat-icon>
                  </button>
                </div>
                @if (showNoteGenerationSettings) {
                  <span class="h-8 w-px bg-outline-variant shrink-0" aria-hidden="true"></span>
                  <div
                    [appTooltip]="styleSettingsTooltip ? (styleSettingsTooltip | translate) : null"
                    appTooltipPosition="above">
                    <button mat-stroked-button
                      (click)="openDetailsAndStyle()"
                      [disabled]="isDictationMode || !canRegenerateDocument || isRegenerating"
                      data-testid="detail-and-style-settings"
                      class="!h-9 text-small !px-4 mx-2">
                        {{ generatedDetailLevelLabel | titlecase | translate }}
                    </button>
                  </div>
                }
                <span class="h-8 w-px bg-outline-variant shrink-0" aria-hidden="true"></span>
                <div class="inline-flex">
                  <button mat-icon-button *ngIf="hasDocuments" (click)="copyContent()" [disabled]="isRegenerating"
                    class="text-[var(--color-primary)] border-[var(--color-primary)] hover:bg-[var(--color-primary)]/5 transition-colors duration-200"
                    [matTooltip]="'Copy content' | translate">
                    <mat-icon class="material-symbols-outlined">content_copy</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <app-regenerate-error
            *ngIf="regenerate.hasError(selectedDocument) && !selectedDocument?.error"
            [disabled]="isRegenerating"
            (retry)="retryProcessing()"
            (dismiss)="onRegenerateErrorDismiss()"
            class="block px-4 pt-3"></app-regenerate-error>
          <!-- Editor Content -->
          <div class="flex-1 relative overflow-auto editor-scroll-container"
            [ngClass]="showToolsPanel && rightPaneOpen
              ? 'max-md:pb-[calc(var(--content-panel-collapsed-height,72px)+env(safe-area-inset-bottom,0px))]'
              : null"
            [style.--content-panel-collapsed-height.px]="contentPanelCollapsedHeightPx">
            <!-- Floating saving spinner over editor area (top-right) -->
            <div *ngIf="isSaving"
              class="absolute right-3 top-3 z-20 flex items-center gap-2 bg-[var(--color-surface)]/80 backdrop-blur px-2 py-1 rounded-full border border-outline-variant shadow-sm">
              <mat-icon class="animate-spin text-on-surface-variant">autorenew</mat-icon>
              <span class="label-medium text-on-surface-variant">{{ 'Saving…' | translate }}</span>
            </div>
            <div #editorContainer class="w-full"></div>
            <div class="md:hidden" data-testid="document-rating-mobile">
              <ng-container *ngTemplateOutlet="documentRatingBar"></ng-container>
            </div>
          </div>
        </div>
        <!-- Editor Section: Error State -->
        <div class="flex-1 flex flex-col min-h-0 overflow-auto bg-white"
          *ngIf="hasDocuments && selectedDocument?.error"
          [ngClass]="showToolsPanel && rightPaneOpen
            ? 'max-md:pb-[calc(var(--content-panel-collapsed-height,72px)+env(safe-area-inset-bottom,0px))]'
            : null"
          [style.--content-panel-collapsed-height.px]="contentPanelCollapsedHeightPx">
          <div class="flex-1 flex items-center justify-center">
            <div class="text-center max-w-md px-6 py-10">
              <div
                class="w-16 h-16 rounded-full bg-[var(--color-error-container)] flex items-center justify-center mx-auto mb-4">
                <mat-icon class="text-error text-4xl">warning</mat-icon>
              </div>
              <h2 class="title-medium text-on-surface mb-2">{{ 'Failed to process the document' | translate }}</h2>
              <p class="body-medium text-on-surface-variant mb-6">{{ 'Please retry to process this document again.' | translate }}</p>
              <button mat-raised-button color="primary" (click)="retryProcessing()" [disabled]="isRegenerating"
                class="bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary)]/90">
                <mat-icon class="mr-2">refresh</mat-icon>
                {{ 'Retry' | translate }}
              </button>
            </div>
          </div>
          <div class="md:hidden" data-testid="document-rating-mobile-error">
            <ng-container *ngTemplateOutlet="documentRatingBar"></ng-container>
          </div>
        </div>
        <!-- Spinner -->
        <div *ngIf="isRegenerating" class="absolute inset-0 bg-white/70 flex flex-col items-center justify-center z-10 rounded-[8px]">
          <img src="assets/spinner.svg" alt="" aria-hidden="true" class="spinner w-[64px] md:w-[96px] h-[64px] md:h-[96px]" />
          <p class="title-medium md:headline-small text-primary mt-8">{{ processingOverlayMessage | translate }}</p>
        </div>
      </div>
      </div>
    </div>

    <app-content-panel
      *ngIf="showToolsPanel && rightPaneOpen"
      [utterances]="transcriptUtterances"
      [notes]="notes"
      [copilot]="dictation!.copilot!"
      [showSyncAllBanner]="showSyncAllBanner"
      [syncAllDisabled]="isSyncAllDisabled"
      [transcriptDeleted]="retention.transcriptDeleted"
      [contextReadonly]="!retention.canGenerate"
      [hideContextNotes]="isSmartDictationActive"
      [isLegalOrg]="isLegalOrg"
      (saveNote)="addNote($event)"
      (deleteNote)="deleteContextNote($event)"
      (syncAll)="regenerateAllDocuments({ trigger: 'sync' })">
    </app-content-panel>
  </div>

  <!-- Bottom Actions (desktop). On mobile these sit in the editor scroll so they can clear the content panel. -->
  <div class="hidden md:block">
    <ng-container *ngTemplateOutlet="documentRatingBar; context: { commandInputId: 'commandSearchInput' }"></ng-container>
  </div>

  <!-- Priority Menu Template -->
  <mat-menu #priorityMenu="matMenu" class="min-w-[220px]">
    <button mat-menu-item (click)="setPriority(1)" [disabled]="isRegenerating">
      <span class="flex items-center gap-3">
        <span class="flex items-center gap-1">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#53DD6C"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#C7CAD1"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#C7CAD1"></span>
        </span>
        <span>{{ 'Low priority' | translate }}</span>
      </span>
    </button>
    <button mat-menu-item (click)="setPriority(2)" [disabled]="isRegenerating">
      <span class="flex items-center gap-3">
        <span class="flex items-center gap-1">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#E59F2A"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#E59F2A"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#C7CAD1"></span>
        </span>
        <span>{{ 'Medium priority' | translate }}</span>
      </span>
    </button>
    <button mat-menu-item (click)="setPriority(3)" [disabled]="isRegenerating">
      <span class="flex items-center gap-3">
        <span class="flex items-center gap-1">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#BA1A1A"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#BA1A1A"></span>
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background-color:#BA1A1A"></span>
        </span>
        <span>{{ 'High priority' | translate }}</span>
      </span>
    </button>
  </mat-menu>
  <!-- More Actions Menu Template -->
  <mat-menu #moreMenu="matMenu">
    <div *ngIf="!selectedDocument?.approved">
      <!-- TDP-1501: Side panel button hidden for release
      <button mat-menu-item (click)="toggleRightPane()" *ngIf="showToolsPanel">
        <mat-icon>view_sidebar</mat-icon>
        <span>{{ rightPaneOpen ? ('Hide content panel' | translate) : ('Show content panel' | translate) }}</span>
      </button>
      -->
      <span
        class="block w-full"
        [appTooltip]="disabledForSmartDictationTooltip ? (disabledForSmartDictationTooltip | translate) : null"
        appTooltipPosition="above">
        <button mat-menu-item (click)="regenerateDocument()" [disabled]="!canRegenerateDocument || isRegenerating"
          class="!w-full">
          <span>{{ 'Regenerate document' | translate }}</span>
        </button>
      </span>
      <button mat-menu-item (click)="deleteNote()" [disabled]="isRegenerating">
        <span>{{ 'Delete document' | translate }}</span>
      </button>
    </div>
  </mat-menu>

  <!-- Floating Record Button Component -->
  <div class="fixed md:z-1000 md:bottom-9 md:right-14 right-2"
    [ngClass]="showToolsPanel && rightPaneOpen
      ? 'bottom-[calc(5rem+env(safe-area-inset-bottom))]'
      : 'bottom-4'">
    <app-record-button *ngIf="dictation && hasDocuments && !selectedDocument?.error && showRecordButton" [dictationId]="dictation._id" [audioPayload]="dictation.audio"
      [mode]="isSmartDictationActive ? 'smartDictation' : 'dictate'"
      [disabled]="isRecordButtonDisabled"
      [disabledTooltip]="dictationDisabledTooltip"
      [saveUtterances]="isSmartDictationActive"
      (transcript)="onIncomingTranscript($event)"
      (recordControlClicked)="onRecordControlClicked($event)">
    </app-record-button>
  </div>
  }
</div>

<mat-menu #documentMenu="matMenu" class="min-w-[220px]">
  <span
    class="block w-full"
    [appTooltip]="disabledForSmartDictationTooltip ? (disabledForSmartDictationTooltip | translate) : null"
    appTooltipPosition="above">
    <button mat-menu-item (click)="regenerateAllDocuments()"
      [disabled]="regenerate.isRegeneratingAll(dictation?._id) || isRegenerating || !canRegenerateAllDocuments"
      class="!w-full">
      {{ 'Regenerate all documents' | translate }}
    </button>
  </span>
  <button mat-menu-item *ngIf="!hasApprovedDocuments()" (click)="handleDeleteEncounter()">
    {{ 'Delete encounter' | translate }}
  </button>
</mat-menu>

<ng-template #documentRatingBar let-commandInputId="commandInputId">
  <div class="bg-white px-3 flex-none">
    <div class="flex items-center">
      <button *ngIf="hasDocuments" mat-icon-button (click)="scoreDocument('up')" [class]="selectedDocument?.rate === 1 ? 'text-primary' : 'text-on-surface-variant'" [matTooltip]="'Thumbs up' | translate"
        [disabled]="isRegenerating">
        <mat-icon [svgIcon]="(selectedDocument?.rate ?? 0) === 1 ? 'thumbs-up-fill' : 'thumbs-up'" class="!text-lg"></mat-icon>
      </button>
      <button *ngIf="hasDocuments" mat-icon-button (click)="scoreDocument('down')" [class]="selectedDocument?.rate === -1 ? 'text-error' : 'text-on-surface-variant'" [matTooltip]="'Thumbs down' | translate"
        [disabled]="isRegenerating">
        <mat-icon [svgIcon]="(selectedDocument?.rate ?? 0) === -1 ? 'thumbs-down-fill' : 'thumbs-down'" class="!text-lg"></mat-icon>
      </button>
      <div *ngIf="showTestCommands && hasDocuments">
        <mat-form-field appearance="outline" class="min-w-[280px] ml-2">
          <mat-label>{{ 'Test command' | translate }}</mat-label>
          <mat-select (closed)="onSelectCommand()" (openedChange)="onCommandsOpened($event)" [(value)]="selectedCommand">
            <mat-option disabled>
              <div class="px-1 py-1 w-full">
                <input [attr.id]="commandInputId" matInput [formControl]="commandFilter" [placeholder]="'Search…' | translate" (keydown)="$event.stopPropagation()" (click)="$event.stopPropagation()" class="w-full" />
              </div>
            </mat-option>
            <mat-option [value]="''">{{ 'Select a command…' | translate }}</mat-option>
            <mat-option *ngFor="let c of filteredCommands" [value]="c.name">{{ c.label }} ({{ c.name }})</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #documentMoreMenu let-class="class">
  <button mat-icon-button [matMenuTriggerFor]="documentMenu" aria-label="More actions" class="icon-gray ml-2"
  [ngClass]="class">
    <mat-icon svgIcon="more-vert"></mat-icon>
  </button>
</ng-template>